import { ArrowLeftOutlined, FileOutlined } from '@ant-design/icons';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { Button, Modal, Progress, Tag } from 'antd';
import React, { FC, useEffect, useState } from 'react';

export interface DocumentType {
    id: string;
    name: string;
    url: string;
    extension?: string;
}

export interface DocumentViewerModalProps {
    document?: DocumentType;
    open: boolean;
    onClose?: () => void;
}

const DocumentViewerModal: FC<DocumentViewerModalProps> = ({ document, open, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [webViewer, setWebViewer] = useState<WebViewerInstance | null>(null);
    const webViewerRef = React.useRef<HTMLDivElement>(null);
    const [isViewerInitialized, setIsViewerInitialized] = useState(false);

    const fullDomain = window.location.origin;

    console.log('fullDomain', `${fullDomain}/webviewer/lib`);

    useEffect(() => {
        let instance: WebViewerInstance | null = null;

        const initWebViewer = async () => {
            if (!webViewerRef.current || isViewerInitialized || !open) return;

            setLoading(true);
            try {
                instance = await WebViewer(
                    {
                        path: `${fullDomain}/webviewer/lib`,
                        isReadOnly: true,
                        enableAnnotations: false,
                        fullAPI: false,
                        enableMeasurement: false,
                        enableRedaction: false,
                        enableOptimizedWorkers: true,
                    },
                    webViewerRef.current,
                );

                instance.Core.documentViewer.enableReadOnlyMode();
                instance.Core.annotationManager.enableReadOnlyMode();

                // Disable unnecessary UI elements
                instance.UI.disableElements(['toolbarGroup-Shapes']);
                instance.UI.disableElements(['toolbarGroup-Edit']);
                instance.UI.disableElements(['toolbarGroup-Insert']);
                instance.UI.disableElements(['toolbarGroup-Annotate']);

                setWebViewer(instance);
                setIsViewerInitialized(true);
            } catch (error) {
                console.error('Error initializing WebViewer:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && !isViewerInitialized) {
            const timeoutId = setTimeout(() => {
                initWebViewer();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        return () => {
            if (!open && instance) {
                instance.UI.closeDocument();
                setWebViewer(null);
                setIsViewerInitialized(false);
            }
        };
    }, [open, isViewerInitialized]);

    useEffect(() => {
        if (!webViewer || !document?.url || !open) return;

        const loadDocument = async () => {
            try {
                await webViewer.UI.loadDocument(document.url, {
                    filename: document.name,
                    extension: document.extension,
                });
            } catch (error) {
                console.error('Error loading document:', error);
            }
        };

        loadDocument();
    }, [webViewer, document, open]);

    const handleClose = () => {
        if (webViewer) {
            webViewer.UI.closeDocument();
            setWebViewer(null);
            setIsViewerInitialized(false);
        }
        onClose?.();
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleClose} />
                    <span>View Document</span>
                    {document && <Tag icon={<FileOutlined />}>{document.name}</Tag>}
                    {loading && (
                        <Progress
                            type="line"
                            percent={100}
                            status="active"
                            showInfo={false}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                            }}
                        />
                    )}
                </div>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            width="100%"
            style={{ top: 0, padding: 0, height: '100vh' }}
            bodyStyle={{ height: 'calc(100vh - 55px)', padding: 0 }}
            closable={false}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
        >
            <div
                ref={webViewerRef}
                style={{
                    width: '100%',
                    height: '100%',
                }}
            />
        </Modal>
    );
};

export default DocumentViewerModal;
